import React, { useState } from 'react';

import { DateTime } from 'luxon';

import { Alert, Breadcrumb, Panel } from '@shared/components/bootstrap';

import { AvailabilityControls } from '@admin/components/availability/controls';
import { PageHeader } from '@admin/components/helpers/page_header';
import { Pagination } from '@admin/components/pagination';
import { Spinner } from '@admin/components/spinner';
import { availabilityLaborAdjustmentNewURL } from '@admin/config/routes';
import { usePermission } from '@admin/hooks/use_permission';
import { useTitle } from '@admin/hooks/use_title';
import { AVAILABILITY_ADMIN } from '@admin/libraries/access_control_catalog.generated';
import { client } from '@admin/libraries/apollo';
import {
  Availability__LaborAdjustmentFiltersInput,
  AvailabilityLaborAdjustmentsDocument,
  useAvailabilityLaborAdjustmentsDeleteMutation,
  useAvailabilityLaborAdjustmentsQuery,
} from '@admin/schema';

import { Entries } from './entries';
import { Filters } from './filters';

const DEFAULT_FILTERS: Availability__LaborAdjustmentFiltersInput = {
  fromDate: DateTime.local().toISODate(),
  tillDate: DateTime.local().plus({ days: 30 }).toISODate(),
  regionIDs: [],
  orderClassifications: [],
};

export const List: React.FC = () => {
  useTitle('Availability Labor Adjustment');
  const [filters, setFilters] = useState<Availability__LaborAdjustmentFiltersInput>(DEFAULT_FILTERS);
  const [page, setPage] = useState<number>();
  const [selectedIDs, setSelectedIDs] = useState<string[]>([]);

  const { data, loading: querying } = useAvailabilityLaborAdjustmentsQuery({
    client,
    variables: {
      page,
      filters: {
        regionIDs: filters.regionIDs,
        fromDate: filters.fromDate,
        tillDate: filters.tillDate,
        orderClassifications: filters.orderClassifications,
      },
    },
  });

  const [destroy, { data: destroyData, loading: destroying }] = useAvailabilityLaborAdjustmentsDeleteMutation({
    client,
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: AvailabilityLaborAdjustmentsDocument,
        variables: {
          page,
          filters: {
            regionIDs: filters.regionIDs,
            fromDate: filters.fromDate,
            tillDate: filters.tillDate,
            orderClassifications: filters.orderClassifications,
          },
        },
      },
    ],
    onCompleted: () => setSelectedIDs([]),
  });

  const onDestroy = async () => {
    await destroy({
      variables: {
        IDs: selectedIDs,
      },
    });
  };

  const onPageChange = (pageNumber: number) => {
    setSelectedIDs([]);
    setPage(pageNumber);
  };

  const canModify = usePermission(AVAILABILITY_ADMIN.resource, AVAILABILITY_ADMIN.actions.update);
  const paginated = data?.paginated;
  const options = paginated?.results.map(({ id }) => id) ?? [];
  const loading = querying || destroying;

  return (
    <>
      <PageHeader>
        <Breadcrumb>
          <Breadcrumb.Item active>Availability Labor Adjustment</Breadcrumb.Item>
        </Breadcrumb>
      </PageHeader>
      <Panel>
        <Panel.Body>
          {destroyData?.result?.error && <Alert style="danger">{destroyData.result.error}</Alert>}
          <Filters filters={filters} onChange={setFilters} />
          <AvailabilityControls
            createHref={availabilityLaborAdjustmentNewURL()}
            loading={loading}
            options={options}
            selectedOptions={selectedIDs}
            create={canModify}
            destroy={canModify}
            onDestroy={onDestroy}
            setSelectedOptions={setSelectedIDs}
          />
          {querying && <Spinner />}
          {paginated && (
            <Entries
              availabilityLaborAdjustments={paginated.results}
              selectedOptions={selectedIDs}
              setSelectedOptions={setSelectedIDs}
            />
          )}
          {paginated && <Pagination onPage={onPageChange} pagination={paginated.pagination} />}
        </Panel.Body>
      </Panel>
    </>
  );
};
